<template>
    <el-drawer class="examination-history-drawer" v-model="drawerVisible" :title="t('oldExaminations')" :size="400">
        <div v-if="examinations.length === 0" class="empty-state">
            <p>{{ t('noExaminations') }}</p>
        </div>
        
        <div v-else class="examinations-list">
            <div 
                v-for="examination in examinations" 
                :key="examination.id"
                class="examination-item flex justify-between"
                @click="selectExamination(examination)"
            >
                <div class="exam-id">
                    {{ examination.externalID }}
                    <ExamTypeBadges :examination="examination"/>
                    <span v-if="examination.id === currentExaminationId" class="current-badge">{{ t('current') }}</span>
                </div>
                <div class="exam-date">{{ formatExaminationDate(examination.createdAt) }}</div>
            </div>
        </div>
    </el-drawer>
    
    <button type="button" @click="drawerVisible = true" class="relative rounded-xl border border-input bg-input p-1 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0">
        <Icon icon="mdi:history" width="24" height="24"/>
        <span v-if="examinations.length > 0" class="absolute -top-1.5 -end-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {{ examinations.length }}
        </span>
    </button>
</template>

<script lang="ts" setup>
import Customer from '@/models/customer/Customer';
import Examination from '@/models/customer/Examination';
import ExamTypeBadges from './ExamTypeBadges.vue';
import { useHTTP } from '@/plugins/http';
import { Icon } from '@iconify/vue/dist/iconify.js';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/utils/date';

const messages = {
  en: {
    oldExaminations: 'Old Examinations',
    noExaminations: 'No examinations found',
    current: 'current'
  },
  ar: {
    oldExaminations: 'الفحوصات القديمة',
    noExaminations: 'لا توجد فحوصات',
    current: 'الحالي'
  }
}

const { t } = useI18n({ messages, useScope: 'global' })

const props = defineProps<{
  customer: Customer
  currentExaminationId?: string
}>()

const emit = defineEmits<{
  examinationSelected: [examination: Examination]
}>()

const drawerVisible = ref(false)
const examinations = ref<Examination[]>([])
const http = useHTTP()

const formatExaminationDate = (date: Date | string | undefined): string => {
  if (!date) return 'No Date'
  try {
    if (date instanceof Date) {
      return formatDate(date, 'DD/MM/YYYY')
    } else {
      return new Date(date).toLocaleDateString('en-GB')
    }
  } catch {
    return 'Invalid Date'
  }
}

const selectExamination = (examination: Examination) => {
  emit('examinationSelected', examination)
  drawerVisible.value = false
}

onMounted(async () => {
  try {
    const url = `${Examination.ENDPOINT}?customer_id=${props.customer.id}`
    const data = await http.get<Examination[]>(url)
    examinations.value = data.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0
      if (!a.createdAt) return 1
      if (!b.createdAt) return -1
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  } catch (error) {
    console.error('Error fetching examinations:', error)
  }
})
</script>

<style scoped>
.empty-state {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.examinations-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.examination-item {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.examination-item:hover {
  background: #e8f4fd;
}

.exam-id {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.exam-date {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.exam-doctor {
  font-size: 0.8rem;
  color: #888;
}

.current-badge {
  display: inline-block;
  margin-inline-start: 0.4rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: #3b82f6;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  vertical-align: middle;
}
</style>